import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface LocalKundli {
    id: string;
    name: string;
    gender: string;
    dob: string;
    birth_time: string;
    location: string;
    latitude: number;
    longitude: number;
    timezone?: string;
    created_at: string;
    kundli_data?: any;
}

export interface KundliFormData {
    name: string;
    dob: string;
    birth_time: string;
    location: string;
    latitude: number;
    longitude: number;
    timezone: string;
    gender: string;
    marital_status: string;
    occupation: string;
}

export interface CompatibilityPersonData {
    name: string;
    dob: string;
    birth_time: string;
    location: string;
    latitude: number;
    longitude: number;
    gender: string;
}

export interface CompatibilityPayload {
    payload1: any;
    payload2: any;
}

interface KundliState {
    // Saved kundlis list
    savedKundlis: LocalKundli[];
    saveKundli: (kundli: Omit<LocalKundli, 'id' | 'created_at'>) => LocalKundli;
    deleteKundli: (id: string) => void;
    updateKundliData: (id: string, data: any) => void;
    clearSavedKundlis: () => void;

    // Current session state
    currentKundliData: any | null;
    currentFormData: KundliFormData | null;
    currentSelectedChart: string;
    currentViewDasha: any[];
    currentSelectedId: string | null;

    // Session actions
    setCurrentKundliData: (data: any | null) => void;
    setCurrentFormData: (data: KundliFormData | null) => void;
    setCurrentSelectedChart: (chart: string) => void;
    setCurrentViewDasha: (dasha: any[]) => void;
    setCurrentSelectedId: (id: string | null) => void;
    clearCurrentSession: () => void;

    // Compatibility session state
    compatibilityResults: any | null;
    compatibilityPerson1: CompatibilityPersonData | null;
    compatibilityPerson2: CompatibilityPersonData | null;
    compatibilityPayloads: CompatibilityPayload | null;

    // Compatibility actions
    setCompatibilityResults: (data: any | null) => void;
    setCompatibilityPerson1: (data: CompatibilityPersonData | null) => void;
    setCompatibilityPerson2: (data: CompatibilityPersonData | null) => void;
    setCompatibilityPayloads: (data: CompatibilityPayload | null) => void;
    clearCompatibilitySession: () => void;
}

export const useKundliStore = create<KundliState>()(
    persist(
        (set, get) => ({
            savedKundlis: [],

            saveKundli: (kundli) => {
                const newKundli: LocalKundli = {
                    ...kundli,
                    id: `local_${Date.now()}`,
                    created_at: new Date().toISOString()
                };

                const existing = get().savedKundlis;
                const isDuplicate = existing.some(k =>
                    k.name === newKundli.name && k.dob === newKundli.dob && k.birth_time === newKundli.birth_time
                );

                if (!isDuplicate) {
                    set({ savedKundlis: [newKundli, ...existing].slice(0, 20) });
                }
                return newKundli;
            },

            deleteKundli: (id) => {
                set({ savedKundlis: get().savedKundlis.filter(k => k.id !== id) });
            },

            updateKundliData: (id, data) => {
                set({
                    savedKundlis: get().savedKundlis.map(k =>
                        k.id === id ? { ...k, kundli_data: data } : k
                    )
                });
            },

            clearSavedKundlis: () => set({ savedKundlis: [] }),

            // Current session state
            currentKundliData: null,
            currentFormData: null,
            currentSelectedChart: 'D1',
            currentViewDasha: [],
            currentSelectedId: null,

            // Session actions
            setCurrentKundliData: (data) => set({ currentKundliData: data }),
            setCurrentFormData: (data) => set({ currentFormData: data }),
            setCurrentSelectedChart: (chart) => set({ currentSelectedChart: chart }),
            setCurrentViewDasha: (dasha) => set({ currentViewDasha: dasha }),
            setCurrentSelectedId: (id) => set({ currentSelectedId: id }),
            clearCurrentSession: () => set({
                currentKundliData: null,
                currentFormData: null,
                currentSelectedChart: 'D1',
                currentViewDasha: [],
                currentSelectedId: null,
            }),

            // Compatibility session state
            compatibilityResults: null,
            compatibilityPerson1: null,
            compatibilityPerson2: null,
            compatibilityPayloads: null,

            // Compatibility actions
            setCompatibilityResults: (data) => set({ compatibilityResults: data }),
            setCompatibilityPerson1: (data) => set({ compatibilityPerson1: data }),
            setCompatibilityPerson2: (data) => set({ compatibilityPerson2: data }),
            setCompatibilityPayloads: (data) => set({ compatibilityPayloads: data }),
            clearCompatibilitySession: () => set({
                compatibilityResults: null,
                compatibilityPerson1: null,
                compatibilityPerson2: null,
                compatibilityPayloads: null,
            }),
        }),
        {
            name: 'kundli-pwa-storage',
        }
    )
);
